import React, { useMemo, useState } from 'react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ChartContainer, ChartConfig } from './ChartContainer';
import { ChartWrapper } from './ChartWrapper';
import { ChartTooltipContent } from './ChartTooltip';
import { ChartLegendContent } from './ChartLegend';
import { defaultPalette } from '../../lib/chartPalette';
import { sanitizeForCSSVar } from '../../lib/chartCssUtils';

export interface PieChartProps {
  data: any[];
  config?: ChartConfig;
  title?: string;
  description?: string;
  dataKey: string;
  nameKey: string;
  showTooltip?: boolean;
  showLegend?: boolean;
  height?: number;
  className?: string;
  tooltipFormatter?: (value: any) => string;
  innerRadius?: number;
  outerRadius?: number;
  paddingAngle?: number;
  label?: boolean;
  labelFormatter?: (entry: any) => string;
  /** Enable expand button to view chart in fullscreen dialog */
  expandable?: boolean;
  /** Custom height for the expanded chart in the dialog */
  expandedHeight?: number;
  /** @internal Set by ChartWrapper to indicate expanded view */
  isExpanded?: boolean;
}

// Helper to auto-generate config from data
const useChartDefaults = (
  data: any[],
  nameKey: string,
  providedConfig?: ChartConfig
) => {
  return useMemo(() => {
    if (!data || data.length === 0) {
      return providedConfig || {};
    }

    // Use provided config or auto-generate with default (violet) palette colors
    const config = providedConfig || data.reduce((acc, item, index) => {
      const name = item[nameKey];
      acc[name] = {
        label: name.charAt(0).toUpperCase() + name.slice(1), // Capitalize first letter
        color: defaultPalette[index % defaultPalette.length],
      };
      return acc;
    }, {} as ChartConfig);

    return config;
  }, [data, nameKey, providedConfig]);
};

export const PieChart: React.FC<PieChartProps> = ({
  data,
  config: providedConfig,
  title,
  description,
  dataKey,
  nameKey,
  showTooltip = true,
  showLegend = true,
  height = 350,
  className,
  tooltipFormatter,
  innerRadius = 0,
  outerRadius = 80,
  paddingAngle = 0,
  label = false,
  labelFormatter,
  expandable = true,
  expandedHeight = 500,
  isExpanded = false,
}) => {
  const config = useChartDefaults(data, nameKey, providedConfig);
  const COLORS = Object.keys(config).map(
    (key) => config[key].color || `var(--color-${sanitizeForCSSVar(key)})`
  );
  const [hoveredDataKey, setHoveredDataKey] = useState<string | null>(null);

  // In expanded view, increase radius for better visibility
  const effectiveOuterRadius = isExpanded ? 140 : outerRadius;
  const effectiveInnerRadius = isExpanded && innerRadius > 0 ? 70 : innerRadius;

  const chartContent = (
    <ChartContainer config={config}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsPieChart>
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            innerRadius={effectiveInnerRadius}
            outerRadius={effectiveOuterRadius}
            paddingAngle={paddingAngle}
            label={label ? labelFormatter : false}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  config[entry[nameKey]]?.color ||
                  COLORS[index % COLORS.length]
                }
                opacity={
                  hoveredDataKey === null || hoveredDataKey === entry[nameKey] ? 1 : 0.3
                }
              />
            ))}
          </Pie>
          {showTooltip && (
            <Tooltip
              content={
                <ChartTooltipContent
                  valueFormatter={tooltipFormatter}
                  hideIndicator
                />
              }
            />
          )}
          {showLegend && (
            <Legend
              content={
                <ChartLegendContent
                  onMouseEnter={(dataKey) => setHoveredDataKey(dataKey)}
                  onMouseLeave={() => setHoveredDataKey(null)}
                />
              }
            />
          )}
        </RechartsPieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );

  return (
    <ChartWrapper
      title={title}
      description={description}
      className={className}
      expandable={expandable}
      expandedHeight={expandedHeight}
      chartComponent={PieChart}
      chartProps={{
        data,
        config: providedConfig,
        dataKey,
        nameKey,
        showTooltip,
        showLegend,
        height,
        tooltipFormatter,
        innerRadius,
        outerRadius,
        paddingAngle,
        label,
        labelFormatter,
      }}
    >
      {chartContent}
    </ChartWrapper>
  );
};
